import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { MEDIA_QUALITY_SECTION_PERMISSIONS } from './getPermittedMediaQuality.helper';

import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Section = keyof typeof MEDIA_QUALITY_SECTION_PERMISSIONS;

type DefaultMediaQuality = TargetingBaseType['mediaQuality'] & {
  unknownIncluded: Partial<TargetingBaseType['unknownIncluded']>;
};

// Resetting the whole section only clears what this user can see: sections hidden by permissions keep their
// configuration and, while any of them is enabled, the root flag stays enabled through them
export function getMediaQualityResetValues(
  targeting: TargetingBaseType,
  user: UserInterface,
  userRoles: UserRoleInterface[],
  defaultMediaQuality: DefaultMediaQuality
): Pick<TargetingBaseType, 'mediaQuality' | 'unknownIncluded'> {
  const hiddenSections = (Object.keys(MEDIA_QUALITY_SECTION_PERMISSIONS) as Section[]).filter(
    section => !checkUserResourcePermissions(MEDIA_QUALITY_SECTION_PERMISSIONS[section], user, userRoles)
  );
  const hasHiddenSectionEnabled = hiddenSections.some(section => targeting.mediaQuality?.[section]?.enabled);
  if (!hasHiddenSectionEnabled) {
    return {
      mediaQuality: defaultMediaQuality as TargetingBaseType['mediaQuality'],
      unknownIncluded: { ...targeting.unknownIncluded, ...defaultMediaQuality.unknownIncluded }
    };
  }
  const keptSections = Object.fromEntries(hiddenSections.map(section => [section, targeting.mediaQuality[section]]));
  return {
    mediaQuality: { ...defaultMediaQuality, ...keptSections, enabled: true } as TargetingBaseType['mediaQuality'],
    // The unknown included viewability thresholds belong to viewability prebid
    unknownIncluded: hiddenSections.includes('viewabilityPrebid')
      ? targeting.unknownIncluded
      : { ...targeting.unknownIncluded, ...defaultMediaQuality.unknownIncluded }
  };
}
